import { FC, MouseEvent, useRef } from 'react';
import {
  Card,
  Button,
  DocumentIcon,
  ProgressSpinner,
  ExclamationTriangleIcon,
  MenuItem,
  Menu,
} from '@repo/atomic-components';
import { classNames } from 'primereact/utils';
import { format } from 'date-fns';
import { NavLink } from 'react-router';
import type { TemplateNode } from '@/api';

export interface AiProcessedCardProps {
  name: string;
  pages: number | null;
  entries: number | null;
  updatedDate: string | null;
  createdDate: string | null;
  routeTo: string;
  handleDownload?: (event: MouseEvent<HTMLButtonElement>) => void;
  handleDownloadIndex?: (templateId?: string) => void;
  downloadButtonLabel?: string;
  handleBundleRequestDownloadManually?: (withPageNumbers: boolean) => void;
  handleDownloadWithPageNumberChoice?: (returnFullBundlePageNumbers: boolean, templateId?: string) => void;
  downloadProgress?: number | null;
  isDownloadLoading?: boolean;
  isDownloadError?: boolean;
  isGenerating?: boolean;
  bundleRequestError?: string | null;
  indexDownloadError?: string | null;
  /** Custom CHRONOLOGY templates — appended to the page-number-choice menu. */
  chronologyTemplates?: TemplateNode[];
  /** Custom INDEX templates — appended to the DOCX-index download menu. */
  indexTemplates?: TemplateNode[];
}

export const AiProcessedCard: FC<AiProcessedCardProps> = ({
  name,
  pages,
  entries,
  updatedDate,
  createdDate,
  routeTo,
  handleDownload,
  handleDownloadIndex,
  downloadButtonLabel,
  handleBundleRequestDownloadManually,
  handleDownloadWithPageNumberChoice,
  downloadProgress,
  isDownloadLoading,
  isDownloadError,
  isGenerating,
  bundleRequestError,
  indexDownloadError,
  chronologyTemplates = [],
  indexTemplates = [],
}) => {
  const requestDownloadManuallyMenu = useRef<Menu>(null);
  const pageNumberChoiceMenu = useRef<Menu>(null);
  const indexDownloadMenu = useRef<Menu>(null);

  const requestDownloadManuallyMenuModel: MenuItem[] = [
    {
      label: 'Not numbered',
      command: () => {
        if (handleBundleRequestDownloadManually) {
          handleBundleRequestDownloadManually(false);
        }
      },
    },
    {
      label: 'Bates numbered',
      command: () => {
        if (handleBundleRequestDownloadManually) {
          handleBundleRequestDownloadManually(true);
        }
      },
    },
  ];

  // Chronology menu: two default (no template) options, then — per custom
  // CHRONOLOGY template — a Light and a Full bundle-page-numbers variant, each
  // passing the template id.
  const pageNumberChoiceMenuModel: MenuItem[] = [
    {
      label: 'Default template (Light Bundle Page Numbers)',
      command: () => handleDownloadWithPageNumberChoice?.(false),
    },
    {
      label: 'Default template (Full Bundle Page Numbers)',
      command: () => handleDownloadWithPageNumberChoice?.(true),
    },
    ...chronologyTemplates.flatMap((template) => [
      { separator: true },
      {
        label: `${template.templateName} (Light Bundle Page Numbers)`,
        command: () => handleDownloadWithPageNumberChoice?.(false, template.templateId),
      },
      {
        label: `${template.templateName} (Full Bundle Page Numbers)`,
        command: () => handleDownloadWithPageNumberChoice?.(true, template.templateId),
      },
    ]),
  ];

  // Index menu: a default (no template) download, then one entry per custom
  // INDEX template (passing its id).
  const indexDownloadMenuModel: MenuItem[] = [
    {
      label: 'Default template',
      command: () => handleDownloadIndex?.(),
    },
    ...(indexTemplates.length > 0
      ? [
          { separator: true },
          ...indexTemplates.map((template) => ({
            label: template.templateName,
            command: () => handleDownloadIndex?.(template.templateId),
          })),
        ]
      : []),
  ];

  const handleShowRequestDownloadManuallyMenuClick = (event: MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    if (requestDownloadManuallyMenu.current) {
      requestDownloadManuallyMenu.current?.toggle(event);
    }
  };

  const handleShowPageNumberChoiceMenuClick = (event: MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    if (pageNumberChoiceMenu.current) {
      pageNumberChoiceMenu.current?.toggle(event);
    }
  };

  const handleShowIndexDownloadMenuClick = (event: MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    indexDownloadMenu.current?.toggle(event);
  };

  const renderActionButton = () => {
    // Show error state if download failed
    if (isDownloadError) {
      return (
        <div className="flex items-start justify-start">
          <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />
          <span className="pl-2 text-[14px]/[20px] font-[400] text-red-500">
            Bundle Generation failed, please refresh the page and try again
          </span>
        </div>
      );
    }

    // Show download progress if downloading
    if (typeof downloadProgress === 'number') {
      return (
        <div className="flex items-start justify-start">
          <ProgressSpinner style={{ width: '20px', height: '20px' }} strokeWidth="4" animationDuration="1.5s" />
          <span className="text-fletchers-green-900 pl-2 text-[14px]/[20px] font-[400]">
            Downloading... ({downloadProgress}%)
          </span>
        </div>
      );
    }

    // Show generating state if generating
    if (isGenerating || isDownloadLoading) {
      return (
        <div className="flex items-start justify-start">
          <ProgressSpinner style={{ width: '20px', height: '20px' }} strokeWidth="4" animationDuration="1.5s" />
          <span className="text-fletchers-green-900 pl-2 text-[14px]/[20px] font-[400]">
            Generating PDF. This may take a while...
          </span>
        </div>
      );
    }

    // Show bundle request error in red (when request-download endpoint fails)
    if (bundleRequestError) {
      return (
        <div className="flex items-start justify-start">
          <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />
          <span className="pl-2 text-[14px]/[20px] font-[400] text-red-500">{bundleRequestError}</span>
        </div>
      );
    }

    // Show download button if available (when generation is complete)
    if (handleDownload) {
      return (
        <Button
          icon="pi pi-download"
          onClick={handleDownload}
          aria-label={downloadButtonLabel || 'Download PDF'}
          label={downloadButtonLabel || 'Download PDF'}
          size="small"
          pt={{
            root: {
              className: 'bg-transparent text-fletchers-green-900 hover:text-fletchers-white',
            },
          }}
        />
      );
    }

    // Show download with page number choice dropdown (chronology)
    if (handleDownloadWithPageNumberChoice) {
      return (
        <>
          <Button
            icon="pi pi-angle-down"
            iconPos="right"
            onClick={handleShowPageNumberChoiceMenuClick}
            aria-label={downloadButtonLabel || 'Download DOCX'}
            aria-controls="popup_menu_page_number_choice"
            aria-haspopup
            label={downloadButtonLabel || 'Download DOCX'}
            size="small"
            pt={{
              root: {
                className: 'bg-transparent text-fletchers-green-900 hover:text-fletchers-white',
              },
            }}
          />
          <Menu
            model={pageNumberChoiceMenuModel}
            popup
            ref={pageNumberChoiceMenu}
            id="popup_menu_page_number_choice"
            popupAlignment="right"
            pt={{
              root: { className: 'w-auto' },
              action: { className: 'text-xs px-2' },
            }}
          />
        </>
      );
    }

    // Show generate button by default
    if (handleBundleRequestDownloadManually) {
      return (
        <>
          <Button
            icon="pi pi-angle-down"
            iconPos="right"
            onClick={handleShowRequestDownloadManuallyMenuClick}
            aria-label="Generate bundle PDF"
            aria-controls="popup_menu_request_download_manually"
            aria-haspopup
            label="Generate bundle PDF"
            size="small"
            pt={{
              root: {
                className: 'bg-transparent text-fletchers-green-900 hover:text-fletchers-white',
              },
            }}
          />
          <Menu
            model={requestDownloadManuallyMenuModel}
            popup
            ref={requestDownloadManuallyMenu}
            id="popup_menu_request_download_manually"
            popupAlignment="right"
            pt={{
              root: { className: 'w-30' },
              action: { className: 'text-xs px-2' },
            }}
          />
        </>
      );
    }

    return null;
  };

  return (
    <NavLink to={routeTo} target="_blank" rel="noopener noreferrer">
      <Card
        pt={{
          root: {
            className:
              'bg-fletchers-green-50 shadow-none cursor-pointer hover:bg-fletchers-green-100 h-full w-full min-h-[290px]',
          },
          body: { className: 'p-0 h-full w-full' },
          content: { className: 'flex flex-col h-full w-full' },
        }}
      >
        <div className="flex h-16 items-start justify-start gap-2 px-3 pt-3">
          {renderActionButton()}
          {handleDownloadIndex && !indexDownloadError && (
            <>
              <Button
                icon="pi pi-angle-down"
                iconPos="right"
                onClick={handleShowIndexDownloadMenuClick}
                aria-label="Download index"
                aria-controls="popup_menu_index_download"
                aria-haspopup
                label="Download index"
                size="small"
                pt={{
                  root: {
                    className: 'bg-transparent text-fletchers-green-900 hover:text-fletchers-white',
                  },
                }}
              />
              <Menu
                model={indexDownloadMenuModel}
                popup
                ref={indexDownloadMenu}
                id="popup_menu_index_download"
                popupAlignment="right"
                pt={{
                  root: { className: 'w-auto' },
                  action: { className: 'text-xs px-2' },
                }}
              />
            </>
          )}
          {indexDownloadError && (
            <span className="text-[14px]/[20px] font-[400] text-red-500">{indexDownloadError}</span>
          )}
        </div>

        <div className={classNames('flex h-full flex-col items-center justify-center')}>
          <DocumentIcon className="text-fletchers-green-500" />
          <span className="mt-8 text-xl/5 font-semibold">{name}</span>
        </div>

        <div className="flex w-full items-center justify-between self-end px-7 pb-6">
          {pages !== null && (
            <div>
              <h6 className="text-fletchers-gray-200 text-xs/5">Pages</h6>
              <p className="text-xs/5 font-semibold">{pages}</p>
            </div>
          )}
          {entries !== null && (
            <div>
              <h6 className="text-fletchers-gray-200 text-xs/5">Entries</h6>
              <p className="text-xs/5 font-semibold">{entries}</p>
            </div>
          )}
          {updatedDate !== null && (
            <div>
              <h6 className="text-fletchers-gray-200 text-xs/5">Updated Date</h6>
              <p className="text-xs/5 font-semibold">{format(new Date(updatedDate), 'HH:mm d MMM y')}</p>
            </div>
          )}
          {createdDate !== null && (
            <div>
              <h6 className="text-fletchers-gray-200 text-xs/5">Created Date</h6>
              <p className="text-xs/5 font-semibold">{format(new Date(createdDate), 'HH:mm d MMM y')}</p>
            </div>
          )}
        </div>
      </Card>
    </NavLink>
  );
};
